import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import SEOHead from '../components/SEOHead';

const PolitiqueConfidentialite = () => {
  const { t } = useTranslation();

  return (
    <>
      <SEOHead 
        title="Politique de Confidentialité - Golden Mali Suite"
        description="Politique de confidentialité et protection des données personnelles de la plateforme Golden Mali Suite"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-navy-800 mb-6">
            Politique de Confidentialité
          </h1>
          
          <div className="prose max-w-none text-gray-700 space-y-6">
            <p className="text-sm text-gray-500 mb-8">
              <strong>Dernière mise à jour :</strong> Février 2026
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">1. Introduction</h2>
              <p>
                La présente Politique de Confidentialité décrit la manière dont <strong>GOLDEN MALI SARL</strong> 
                collecte, utilise, stocke et protège les données personnelles des utilisateurs de la plateforme 
                SaaS <strong>Golden Mali Suite</strong>.
              </p>
              <p>
                Cette politique est conforme au Règlement Général sur la Protection des Données (RGPD) et à 
                la loi Informatique et Libertés.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">2. Responsable du Traitement</h2>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p><strong>Responsable du traitement :</strong> GOLDEN MALI SARL</p>
                <p><strong>Gérant :</strong> FOFANA Ibrahima</p>
                <p><strong>Adresse :</strong> Imm DJOGUIBA, 2ème étage, bureau C4, Grand marché, Bamako, République du Mali</p>
                <p><strong>Téléphone :</strong> +223 20 22 58 56 / +223 66 92 25 45</p>
                <p><strong>Email :</strong> support@golden-mali.com</p>
                <p><strong>Site web :</strong> https://golden-mali.cloud</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>Développement et Maintenance :</strong> Créatif-web</p>
                <p><strong>Statut :</strong> Entrepreneur Individuel (EI)</p>
                <p><strong>Adresse :</strong> 16 rue Vendôme, 13500 Martigues, France</p>
                <p><strong>Téléphone :</strong> 06.10.56.25.06</p>
                <p><strong>Email :</strong> contact@creatifboost.com</p>
                <p><strong>Site web :</strong> https://creatif-web.eu</p>
                <p><strong>SIRET :</strong> 93466266900019</p>
                <p><strong>SIREN :</strong> 934662669</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">3. Données Collectées</h2>
              <h3 className="text-xl font-semibold text-navy-700 mb-3">3.1 Données d'Identification</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Nom et prénom</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone (si fourni)</li>
                <li>Fonction dans l'entreprise</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">3.2 Données de Connexion</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Adresse IP</li>
                <li>Logs de connexion (date, heure, durée)</li>
                <li>Type de navigateur et système d'exploitation</li>
                <li>Cookies techniques</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">3.3 Données Professionnelles</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Transactions d'achat et de vente d'or</li>
                <li>Positions ouvertes</li>
                <li>Stocks physiques</li>
                <li>Calculs de marges et profits</li>
                <li>Historique des opérations</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">3.4 Données de Paiement SWIFT</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Informations bancaires nécessaires aux transactions SWIFT</li>
                <li>Documents justificatifs (si uploadés)</li>
                <li>Statut des paiements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">4. Finalité du Traitement</h2>
              <p>Les données personnelles sont collectées et traitées pour :</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Gestion des transactions :</strong> Enregistrement et suivi des transactions d'or</li>
                <li><strong>Gestion des positions :</strong> Suivi des positions ouvertes et calcul des marges</li>
                <li><strong>Gestion du stock :</strong> Suivi du stock physique d'or</li>
                <li><strong>Gestion des paiements SWIFT :</strong> Traitement des paiements internationaux</li>
                <li><strong>Sécurisation de l'accès :</strong> Authentification et contrôle d'accès à la plateforme</li>
                <li><strong>Amélioration du service :</strong> Analyse de l'utilisation pour améliorer la plateforme</li>
                <li><strong>Respect des obligations légales :</strong> Traçabilité des transactions, obligations comptables et fiscales</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">5. Base Légale du Traitement</h2>
              <p>Le traitement des données personnelles est fondé sur :</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Exécution d'un contrat :</strong> Utilisation de la plateforme dans le cadre de l'activité professionnelle</li>
                <li><strong>Respect d'obligations légales :</strong> Traçabilité des transactions financières, obligations comptables</li>
                <li><strong>Intérêt légitime :</strong> Sécurisation de la plateforme, prévention de la fraude</li>
                <li><strong>Consentement :</strong> Pour les cookies analytiques (si applicable)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">6. Conservation des Données</h2>
              <p>Les données personnelles sont conservées :</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Pendant toute la durée d'utilisation de la plateforme</strong> par l'utilisateur</li>
                <li><strong>Conformément aux obligations légales de conservation :</strong>
                  <ul className="list-circle pl-6 mt-2 space-y-1">
                    <li>Documents comptables : 10 ans</li>
                    <li>Traçabilité des transactions : selon la réglementation applicable</li>
                    <li>Données de connexion : 12 mois</li>
                  </ul>
                </li>
                <li><strong>Maximum 3 ans après la fin de l'utilisation du compte</strong> (sauf obligation légale contraire)</li>
              </ul>
              <p>
                À l'expiration de ces délais, les données sont supprimées de manière sécurisée ou anonymisées.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">7. Droits des Utilisateurs</h2>
              <p>
                Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
              </p>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">7.1 Droit d'Accès</h3>
              <p>
                Vous pouvez demander l'accès à vos données personnelles et obtenir une copie de celles-ci.
              </p>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">7.2 Droit de Rectification</h3>
              <p>
                Vous pouvez demander la correction de vos données personnelles si elles sont inexactes ou incomplètes.
              </p>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">7.3 Droit à l'Effacement</h3>
              <p>
                Vous pouvez demander la suppression de vos données personnelles, sous réserve des obligations 
                légales de conservation (notamment pour les transactions financières).
              </p>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">7.4 Droit à la Portabilité</h3>
              <p>
                Vous pouvez demander la récupération de vos données dans un format structuré et couramment utilisé.
              </p>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">7.5 Droit d'Opposition</h3>
              <p>
                Vous pouvez vous opposer au traitement de vos données personnelles pour des motifs légitimes.
              </p>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">7.6 Droit à la Limitation</h3>
              <p>
                Vous pouvez demander la limitation du traitement de vos données dans certains cas.
              </p>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">7.7 Exercice des Droits</h3>
              <p>
                Pour exercer vos droits, vous pouvez nous contacter :
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>Par email :</strong> support@golden-mali.com</p>
                <p><strong>Par courrier :</strong> GOLDEN MALI SARL, Imm DJOGUIBA, 2ème étage, bureau C4, Grand marché, Bamako, République du Mali</p>
              </div>
              <p>
                Nous nous engageons à répondre à votre demande dans un délai d'un mois. Si nécessaire, 
                ce délai peut être prolongé de deux mois compte tenu de la complexité de la demande.
              </p>
              <p>
                Vous avez également le droit d'introduire une réclamation auprès de la Commission Nationale 
                de l'Informatique et des Libertés (CNIL) si vous estimez que vos droits ne sont pas respectés.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">8. Sécurité des Données</h2>
              <p>
                GOLDEN MALI SARL met en œuvre des mesures techniques et organisationnelles appropriées pour 
                assurer la sécurité des données personnelles :
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Chiffrement des données :</strong> HTTPS (TLS 1.2+), chiffrement AES-256 pour les données sensibles</li>
                <li><strong>Authentification sécurisée :</strong> Mots de passe robustes, authentification à deux facteurs (2FA) optionnelle</li>
                <li><strong>Sauvegardes automatiques :</strong> Sauvegardes régulières des données</li>
                <li><strong>Accès restreint :</strong> Principe du moindre privilège, accès limité aux personnes autorisées</li>
                <li><strong>Logs d'audit :</strong> Traçabilité des accès et modifications</li>
                <li><strong>Mise à jour de sécurité :</strong> Mises à jour régulières des systèmes et logiciels</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">9. Transfert de Données</h2>
              <p>
                Les données personnelles sont hébergées sur un serveur VPS situé en France, géré par Hostinger.
              </p>
              <p>
                Aucun transfert de données vers des pays tiers n'est effectué sans garanties appropriées 
                conformes au RGPD.
              </p>
              <p>
                En cas de transfert futur vers un pays tiers, nous nous engageons à mettre en place les 
                garanties appropriées (clauses contractuelles types, Privacy Shield, etc.).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">10. Cookies</h2>
              <p>
                La plateforme utilise des cookies techniques nécessaires au fonctionnement du service :
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Cookies de session :</strong> Pour maintenir votre session de connexion</li>
                <li><strong>Cookies de préférences :</strong> Pour mémoriser vos préférences (langue, etc.)</li>
                <li><strong>Cookies de sécurité :</strong> Pour sécuriser l'accès à la plateforme</li>
              </ul>
              <p>
                Ces cookies sont essentiels au fonctionnement de la plateforme et ne nécessitent pas de 
                consentement préalable.
              </p>
              <p>
                La plateforme peut également utiliser des cookies analytiques pour améliorer le service. 
                Ces cookies sont soumis au consentement de l'utilisateur via le bandeau de cookies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">11. Partage des Données</h2>
              <p>
                GOLDEN MALI SARL ne vend, ne loue ni ne partage vos données personnelles avec des tiers, 
                sauf dans les cas suivants :
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Prestataires de services :</strong> Hébergeur (Hostinger), développeur (Créatif-web) dans le cadre de la maintenance</li>
                <li><strong>Obligations légales :</strong> En cas d'obligation légale ou de demande des autorités compétentes</li>
                <li><strong>Avec votre consentement :</strong> Si vous avez donné votre consentement explicite</li>
              </ul>
              <p>
                Tous nos prestataires sont soumis à des obligations de confidentialité strictes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">12. Modification de la Politique</h2>
              <p>
                GOLDEN MALI SARL se réserve le droit de modifier la présente Politique de Confidentialité 
                à tout moment pour refléter les évolutions légales, réglementaires ou techniques.
              </p>
              <p>
                Les utilisateurs sont invités à consulter régulièrement cette page pour prendre connaissance 
                des éventuelles modifications. La date de dernière mise à jour est indiquée en haut de cette page.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">13. Contact</h2>
              <p>
                Pour toute question concernant la présente Politique de Confidentialité ou pour exercer vos droits :
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>GOLDEN MALI SARL</strong></p>
                <p>Imm DJOGUIBA, 2ème étage, bureau C4</p>
                <p>Grand marché, Bamako, République du Mali</p>
                <p><strong>Téléphone :</strong> +223 20 22 58 56 / +223 66 92 25 45</p>
                <p><strong>Email :</strong> support@golden-mali.com</p>
                <p><strong>Site web :</strong> https://golden-mali.cloud</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default PolitiqueConfidentialite;
